import cloneDeep from 'lodash/cloneDeep';

import type { FilterSet, FilterType } from '@webapp/account/src/resources/reports/filter';

import type { NpsType } from '@webapp/common/lib/ui';
import type { Question } from '@webapp/common/resources/entities/question';
import type { AnswerType, QuestionType } from '@webapp/common/resources/survey';

export enum ReportStatus {
    ALL = 1,
    COMPLETED = 2,
    NOT_COMPLETED = 3,
}

export interface Cell {
    id: number;
    str: string;
    count: number;
    percent: number;
    checked: boolean;
    value1: string;
    value2: string;
    value3: string;
    singleValue: string; // user answer
    filterType: FilterType;
    cantRate?: number;
    value?: any; // For Kano category filtering: { category: string }
}

export interface Col {
    id: number;
    headValue: string;
    headChild?: ReactElement;
    filterType: FilterType;
    npsType?: NpsType;
    count: number;
    percent: number;
}

export interface Row {
    id: number;
    value1: string | number;
    value2: string | number;
    value3: string | number;
    child?: ReactElement;
    singleValue: string; // user answer
    cells: Array<Cell>;
    count: number;
    average: number;
    CSAT?: number;
    percent: number;
    importance: number; // rank type only
    order?: number;
    filterType: FilterType;
    type: AnswerType | null;
    npsType: NpsType;
    rowTitleClassname?: string;
    textBlock?: string;
    filePath?: string;
    cantRate?: null;
    forwardParent?: number;
    comment?: string; // comment for each row (used in KANO and CSI)
    // first-click params start
    x?: number;
    y?: number;
    time?: number;
    targetAreaName?: string;
    maxDiffAppears?: number;
    maxDiffBest?: number;
    maxDiffWorst?: number;
    maxDiffBestWorstDiff?: number;
    maxDiffScore?: number;
    // first-click params end
}

export interface Comment {
    id: number;
    comment: string;
    filterType?: FilterType;
}

export interface CommentResponse {
    ID: number;
    COMMENT: string;
}

export interface BaseReport {
    id: number;
    name: string;
    sets: Array<FilterSet>;
    totalCount: number;
    totalCantRate?: number;
    commentsCount: number;
    responsesCount: number;
    order: number;
}

export interface Report extends BaseReport {
    type: QuestionType;
    items: Array<Item>;
    question: Question;
    comments: Array<Comment>;
    addInfo: {
        [key: string]: any;
        totalCSAT?: number;
        totalAvg?: number;
        totalMedian?: number;
        /** KANO features data (from stat.responses.functions) */
        kanoFeatures?: Array<{
            answerId: number;
            title: string;
            counts?: { A?: number; O?: number; M?: number; I?: number; R?: number; Q?: number };
            categories: { A: number; O: number; M: number; I: number; R: number; Q: number };
            finalCategory: string;
            indexS: number;
            indexD: number;
            absD: number;
            zone?: number;
            zoneName: string;
            qPercent: number;
            recommendation: string;
        }>;
    };
    chart?: Array<any>;
    total_cant_rate?: number;
    // Последние загруженные страницы пагинации ("Показать ещё"). Считаем от
    // фактической страницы, а не от количества замапленных строк: маппинг
    // самоответов может отбросить часть строк (FRONT-74 "pagination broken").
    responsesPage?: number;
    commentsPage?: number;
}

export type TMatrixType = 'stars' | 'numbers' | 'numbers-with-borders' | 'slider' | 'rating' | 'max-diff';

export interface Item {
    id: number;
    value: string;
    name?: string;
    filterType: FilterType;
    rows: Array<Row>;
    cols: Array<Col>;
    headerColumns?: Array<{ title: any; size: number }>;
    average: number;
    totalCount: number;
    self: Array<any>;
    matrixType?: TMatrixType;
}

export const initialCol: Readonly<Col> = {
    id: 0,
    filterType: null,
    headValue: '',

    npsType: null,
    count: 0,
    percent: 0,
};

export const initialRow: Readonly<Row> = {
    id: 0,
    value1: '',
    value2: '',
    value3: '',
    singleValue: '',
    cells: [],
    count: 0,
    average: 0,
    percent: 0,
    importance: 0,
    filterType: null,
    type: null,
    npsType: null,
};

export const initialBaseReport: Readonly<BaseReport> = {
    id: 0,
    name: '',
    sets: [],
    totalCount: 0,
    totalCantRate: 0,
    commentsCount: 0,
    responsesCount: 0,
    order: null,
};

export const initialReport: Readonly<Report> = {
    ...cloneDeep(initialBaseReport),
    type: null,
    items: [],
    question: {} as Question,
    comments: [],
    chart: [],
    addInfo: {},
};

export const initialItem: Readonly<Item> = {
    id: 0,
    value: '',
    filterType: null,
    rows: [],
    cols: [],
    average: 0,
    totalCount: 0,
    self: [],
};

export const initialCell: Readonly<Cell> = {
    id: 0,
    str: '',
    count: 0,
    percent: 0,
    checked: false,
    value1: '',
    value2: '',
    value3: '',
    singleValue: '',
    filterType: null,
};
